/**
 * Token-safe page.viewed payload helpers.
 *
 * The matched route *pattern* (e.g. `/s/:token`) is emitted instead of the
 * resolved path, so secret path params (share tokens, recover/verify codes)
 * can never leak into analytics. Query string KEYS are reported but never their
 * values. These are pure functions so the safety guarantees are unit-tested.
 */

/**
 * The pattern template of the most specific matched route record
 * (e.g. `/s/:token`), falling back to the route name when no match info exists.
 * @param {object} target a vue-router route location
 * @returns {string}
 */
export function routePattern(target) {
  if (target && Array.isArray(target.matched) && target.matched.length > 0) {
    return target.matched[target.matched.length - 1]?.path || '';
  }
  return target && target.name ? String(target.name) : '';
}

/**
 * Logical-page identity used to dedupe pageviews. Includes the route pattern and
 * the path params but NOT the query string, so query-only navigations
 * (filters/tabs syncing to the URL) do not emit a new pageview. Kept local; not
 * sent to analytics, so including raw params here is safe.
 * @param {object} to vue-router route location
 * @returns {string}
 */
export function pageViewDedupeKey(to) {
  return `${routePattern(to)}|${JSON.stringify((to && to.params) || {})}`;
}

/**
 * Build the token-safe `page.viewed` event payload.
 * @param {object} to current route location
 * @param {object} [from] previous route location
 * @returns {{resource_type: string, resource_id: string, tool_id?: string, properties: object}}
 */
export function buildPageViewPayload(to, from) {
  const matchedPattern = routePattern(to);
  const toolId = to?.meta?.analytics?.toolId;
  return {
    resource_type: 'page',
    resource_id: String((to && to.name) || matchedPattern || ''),
    ...(toolId ? { tool_id: String(toolId) } : {}),
    properties: {
      route_name: String((to && to.name) || ''),
      matched_pattern: matchedPattern,
      from_pattern: from ? routePattern(from) : null,
      query_keys: Object.keys((to && to.query) || {}),
    },
  };
}
